import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { ApiResponseDTO } from '../interface/app-API.interface';
import {
  AMENDED_RETURN_TAB_ENDPOINTS,
  COMMON_API_ENDPOINTS,
  ISSUES_ENDPOINTS,
  MAINTENANCE_TAB_ENDPOINTS,
  TAX_ACCOUNTING_TAB_ENDPOINTS,
  USERS_TAB_ENDPOINTS,
} from '../constants/app-API.constants';
import {
  IssueIdAPIDTO,
  TaxAccountingFilterRequest,
  TaxAccountingSaveRequest,
} from '../interface/taxAccounting.interface';
import {
  AddTaxRateDTO,
  EntityDTO,
  KeywordDTO,
  MaintenanceAuditTrailDTO,
  TaxRateDTO,
  UpdateTaxRateDTO,
  AddKeywordDTO,
} from '../interface/maintenance.interface';
import { HTTPCommonDTO } from '../interface/app-common.interface';
import { CreateUserDTO, UpdateUserDTO, UserDTO } from '../interface/users.interface';
import { HttpUtilityService } from './app-http.utility.service';
import { AmendmentDTO } from '../interface/amended-return.interface';
import {
  AllIssueDTO,
  AmendedDetailDTO,
  ExistingIssueFilterRequestDTO,
  ExistingIssueRequestDTO,
  ExportExcelPayloadDTO,
  FilterIssueResponse,
  GetIssueDetailsResponse,
  IssueCategoryDTO,
  NewIssueRequestDTO,
  UpdateFilterIssueRequestDTO,
} from '../interface/all-issues.interface';
import { DeleteIssueRequestDTO } from '../interface/audit-trail.interface';
import { FilterLookupResponse } from '../constants/issue-filter.model.constants';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly httpUtilityService = inject(HttpUtilityService);

  /** LOGGED_USER_DETAILS */
  getCurrentUserDetails(): Observable<ApiResponseDTO<UserDTO>> {
    return this.httpUtilityService.get<ApiResponseDTO<UserDTO>>(USERS_TAB_ENDPOINTS.USER.DETAIL);
  }

  /** FILTERS */
  getIssueIDs(): Observable<ApiResponseDTO<IssueIdAPIDTO[]>> {
    return this.httpUtilityService.get<ApiResponseDTO<IssueIdAPIDTO[]>>(
      TAX_ACCOUNTING_TAB_ENDPOINTS.FILTER.issueID,
    );
  }

  getFilters(): Observable<ApiResponseDTO<FilterLookupResponse>> {
    return this.httpUtilityService.get<ApiResponseDTO<FilterLookupResponse>>(
      TAX_ACCOUNTING_TAB_ENDPOINTS.FILTER.GET,
    );
  }

  getTaxAccountingList(payload: TaxAccountingFilterRequest): Observable<ApiResponseDTO<any>> {
    return this.httpUtilityService.post<ApiResponseDTO<any>>(
      TAX_ACCOUNTING_TAB_ENDPOINTS.TAX_ACCOUNTING_LIST.POST,
      payload,
    );
  }

  saveTaxAccountingRecord(payload: TaxAccountingSaveRequest): Observable<ApiResponseDTO<any>> {
    return this.httpUtilityService.put<ApiResponseDTO<any>>(
      TAX_ACCOUNTING_TAB_ENDPOINTS.SAVE.PUT,
      payload,
    );
  }

  downloadExcel(payload: TaxAccountingFilterRequest): Observable<HttpResponse<Blob>> {
    return this.httpUtilityService.postBlob(
      TAX_ACCOUNTING_TAB_ENDPOINTS.EXCEL_DOWNLOAD.POST,
      payload,
    );
  }

  getAuditTrailById(id: string | number): Observable<any> {
    return this.httpUtilityService.get<ApiResponseDTO<any>>(
      `${COMMON_API_ENDPOINTS.AUDIT_TRAIL.GET}/${id}`,
    );
  }

  /** MAINTENANCE TAB API STARTS */

  /** AUDIT TRAIL: GET */
  getAuditTrail(): Observable<ApiResponseDTO<MaintenanceAuditTrailDTO[]>> {
    return this.httpUtilityService.get<ApiResponseDTO<MaintenanceAuditTrailDTO[]>>(
      MAINTENANCE_TAB_ENDPOINTS.AUDIT_TRAIL.GET,
    );
  }

  /** ENTITIES: GET */
  getEntities(): Observable<ApiResponseDTO<EntityDTO[]>> {
    return this.httpUtilityService.get<ApiResponseDTO<EntityDTO[]>>(
      MAINTENANCE_TAB_ENDPOINTS.ENTITES.GET,
    );
  }

  /** ENTITIES: ADD */
  addEntity(payload: EntityDTO): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.post<HTTPCommonDTO>(
      MAINTENANCE_TAB_ENDPOINTS.ENTITES.ADD,
      payload,
    );
  }

  /** ENTITIES: UPDATE */
  updateEntity(payload: EntityDTO): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.put<HTTPCommonDTO>(
      MAINTENANCE_TAB_ENDPOINTS.ENTITES.UPDATE,
      payload,
    );
  }

  /** ENTITIES: DELETE */
  deleteEntity(entityCode: string | number): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.delete<HTTPCommonDTO>(
      `${MAINTENANCE_TAB_ENDPOINTS.ENTITES.DELETE}/${entityCode}`,
    );
  }

  /** KEYWORD: GET */
  getKeywords(): Observable<ApiResponseDTO<KeywordDTO[]>> {
    return this.httpUtilityService.get<ApiResponseDTO<KeywordDTO[]>>(
      MAINTENANCE_TAB_ENDPOINTS.KEYWORD.GET,
    );
  }

  /** KEYWORD: ADD */
  addKeyword(payload: AddKeywordDTO): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.post<HTTPCommonDTO>(
      MAINTENANCE_TAB_ENDPOINTS.KEYWORD.ADD,
      payload,
    );
  }

  /** KEYWORD: UPDATE */
  updateKeyword(payload: KeywordDTO): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.put<HTTPCommonDTO>(
      MAINTENANCE_TAB_ENDPOINTS.KEYWORD.UPDATE,
      payload,
    );
  }

  /** KEYWORD: DELETE */
  deleteKeyword(id: string | number): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.delete<HTTPCommonDTO>(
      `${MAINTENANCE_TAB_ENDPOINTS.KEYWORD.DELETE}/${id}`,
    );
  }

  /** TAX RATE: GET */
  getTaxRates(): Observable<ApiResponseDTO<TaxRateDTO[]>> {
    return this.httpUtilityService.get<ApiResponseDTO<TaxRateDTO[]>>(
      MAINTENANCE_TAB_ENDPOINTS.TAX_RATE.GET,
    );
  }

  /** TAX RATE: ADD */
  addTaxRate(payload: AddTaxRateDTO): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.post<HTTPCommonDTO>(
      MAINTENANCE_TAB_ENDPOINTS.TAX_RATE.ADD,
      payload,
    );
  }

  /** TAX RATE: UPDATE */
  updateTaxRate(payload: UpdateTaxRateDTO): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.put<HTTPCommonDTO>(
      MAINTENANCE_TAB_ENDPOINTS.TAX_RATE.UPDATE,
      payload,
    );
  }

  /** TAX RATE: DELETE */
  deleteTaxRate(id: number): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.delete<HTTPCommonDTO>(
      `${MAINTENANCE_TAB_ENDPOINTS.TAX_RATE.DELETE}/${id}`,
    );
  }

  /** USERS TAB API STARTS */
  /** USERS: GET */
  getUsers(): Observable<ApiResponseDTO<UserDTO[]>> {
    return this.httpUtilityService.get<ApiResponseDTO<UserDTO[]>>(USERS_TAB_ENDPOINTS.USER.GET);
  }

  /** USERS: ADD */
  createUser(payload: CreateUserDTO): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.post<HTTPCommonDTO>(USERS_TAB_ENDPOINTS.USER.ADD, payload);
  }

  /** USERS: UPDATE */
  updateUser(payload: UpdateUserDTO): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.put<HTTPCommonDTO>(USERS_TAB_ENDPOINTS.USER.UPDATE, payload);
  }

  /** USERS: DELETE */
  deleteUser(id: number): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.delete<HTTPCommonDTO>(
      `${USERS_TAB_ENDPOINTS.USER.DELETE}/${id}`,
    );
  }

  /** AMENDED_RETURN: GET */
  getAmendedReturn(): Observable<ApiResponseDTO<AmendmentDTO[]>> {
    return this.httpUtilityService.get<ApiResponseDTO<AmendmentDTO[]>>(
      AMENDED_RETURN_TAB_ENDPOINTS.GET,
    );
  }

  /** AMENDED_RETURN: ADD */
  addAmendedReturn(payload: AmendmentDTO): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.post<HTTPCommonDTO>(AMENDED_RETURN_TAB_ENDPOINTS.ADD, payload);
  }

  /** AMENDED_RETURN: UPDATE */
  updateAmendedReturn(payload: AmendmentDTO): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.put<HTTPCommonDTO>(AMENDED_RETURN_TAB_ENDPOINTS.UPDATE, payload);
  }

  /** AMENDED_RETURN: DELETE */
  deleteAmendedReturn(amendedId: string): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.delete<HTTPCommonDTO>(
      `${AMENDED_RETURN_TAB_ENDPOINTS.DELETE}/${amendedId}`,
    );
  }

  /** ALL_ISSUES: GET */
  getAllIssuesList(): Observable<ApiResponseDTO<AllIssueDTO[]>> {
    return this.httpUtilityService.get<ApiResponseDTO<AllIssueDTO[]>>(
      ISSUES_ENDPOINTS.ALL_ISSUES_TAB_ENDPOINTS.GET,
    );
  }

  /** ALL_ISSUES: GETBYID */
  getAllIssuesDetailsById(issueId: string): Observable<ApiResponseDTO<GetIssueDetailsResponse>> {
    return this.httpUtilityService.get<ApiResponseDTO<GetIssueDetailsResponse>>(
      `${ISSUES_ENDPOINTS.ALL_ISSUES_TAB_ENDPOINTS.GETBYID}/${issueId}`,
    );
  }

  /** ALL_ISSUES: DELETE */
  deleteIssueInAll(issueID: string): Observable<HTTPCommonDTO> {
    return this.httpUtilityService.delete<HTTPCommonDTO>(
      `${ISSUES_ENDPOINTS.ALL_ISSUES_TAB_ENDPOINTS.DELETE}/${issueID}`,
    );
  }

  getCategoriesList(): Observable<ApiResponseDTO<IssueCategoryDTO[]>> {
    const endpoint = ISSUES_ENDPOINTS.NEW_ISSUE_ENDPOINTS.GET_CATE;
    return this.httpUtilityService.get<ApiResponseDTO<IssueCategoryDTO[]>>(endpoint);
  }

  generateIssueID(): Observable<ApiResponseDTO<string>> {
    const endpoint = ISSUES_ENDPOINTS.NEW_ISSUE_ENDPOINTS.GET_ISSUE_ID;
    return this.httpUtilityService.get<ApiResponseDTO<string>>(endpoint);
  }

  saveUpdateNewIssue(payload: NewIssueRequestDTO): Observable<ApiResponseDTO<string>> {
    const endpoint = ISSUES_ENDPOINTS.NEW_ISSUE_ENDPOINTS.POST_PUT_ISSUE;
    return this.httpUtilityService.post<ApiResponseDTO<string>>(endpoint, payload);
  }

  saveUpdateExistingIssue(
    payload: ExistingIssueRequestDTO,
  ): Observable<ApiResponseDTO<ExistingIssueRequestDTO>> {
    const endpoint = ISSUES_ENDPOINTS.EXISTING_ISSUE_ENDPOINTS.POST;
    return this.httpUtilityService.post<ApiResponseDTO<ExistingIssueRequestDTO>>(endpoint, payload);
  }

  getIssueById(issueId: string): Observable<ApiResponseDTO<AllIssueDTO>> {
    const endpoint = `${COMMON_API_ENDPOINTS.ISSUE.GET_ID}/${issueId}`;
    return this.httpUtilityService.get<ApiResponseDTO<AllIssueDTO>>(endpoint);
  }

  getFilterDetails(
    payload: ExistingIssueFilterRequestDTO,
  ): Observable<ApiResponseDTO<FilterIssueResponse>> {
    const endpoint = ISSUES_ENDPOINTS.FILTERS_ENDPOINTS.POST_GET;
    return this.httpUtilityService.post<ApiResponseDTO<FilterIssueResponse>>(endpoint, payload);
  }

  updateFilterIssue(payload: UpdateFilterIssueRequestDTO): Observable<ApiResponseDTO<unknown>> {
    const endpoint = ISSUES_ENDPOINTS.FILTERS_ENDPOINTS.POST_GET;
    return this.httpUtilityService.post<ApiResponseDTO<FilterIssueResponse>>(endpoint, payload);
  }

  getARFillingById(year: number): Observable<ApiResponseDTO<AmendedDetailDTO[]>> {
    const endpoint = `${ISSUES_ENDPOINTS.EXISTING_ISSUE_ENDPOINTS.GET_FILLING}/${year}`;
    return this.httpUtilityService.get<ApiResponseDTO<AmendedDetailDTO[]>>(endpoint);
  }

  updateIssueFilterDetails(
    payload: UpdateFilterIssueRequestDTO,
  ): Observable<ApiResponseDTO<unknown>> {
    const endpoint = ISSUES_ENDPOINTS.EXISTING_ISSUE_ENDPOINTS.PUT_EDIT;
    return this.httpUtilityService.put<ApiResponseDTO<FilterIssueResponse>>(endpoint, payload);
  }

  cancelIssueAdj(payload: DeleteIssueRequestDTO): Observable<ApiResponseDTO<unknown>> {
    const endpoint = ISSUES_ENDPOINTS.EXISTING_ISSUE_ENDPOINTS.PUT_CANCEL;
    return this.httpUtilityService.put<ApiResponseDTO<unknown>>(endpoint, payload);
  }

  downloadExcelOfSelectedIssue(payload: ExportExcelPayloadDTO): Observable<HttpResponse<Blob>> {
    const path = COMMON_API_ENDPOINTS.EXCEL.POST_DOWNLOAD;
    return this.httpUtilityService.postBlob(path, payload);
  }

  downloadAllIssuesExcel(): Observable<HttpResponse<Blob>> {
    return this.httpUtilityService.getBlob(ISSUES_ENDPOINTS.ALL_ISSUES_TAB_ENDPOINTS.GET_EXCEL);
  }
}
